/**
 * High-performance subscribed symbols store
 * Initialized from useQuotesWatchlistStore
 */

import { create } from "zustand";
import type {
  SubscribedSymbolsState,
  SubscribedSymbolsStore,
  SubscribedSymbolEntry,
  Quote,
} from "../types";
import type { Symbol } from "../../symbols/types";

const initialState: SubscribedSymbolsState = {
  byCurrency: new Map(),
  isInitialized: false,
  errorMessage: null,
  lastSyncDate: null,
  allSubscribed: [],
  manuallySubscribed: [],
  withOpenTrades: [],
};

// Helper function to compute expensive arrays
// Symbol keys seen on incoming quotes that had no matching byCurrency entry — used to
// debug-log each unknown key once (quote-key divergence would otherwise be invisible).
const unknownQuoteKeys = new Set<string>();

const computeArrays = (byCurrency: Map<string, SubscribedSymbolEntry>) => {
  const allSubscribed = Array.from(byCurrency.values()).sort((a, b) =>
    a.symbol.currency.localeCompare(b.symbol.currency)
  );

  const manuallySubscribed = allSubscribed.filter(
    (entry) => entry.isManuallySubscribed
  );

  const withOpenTrades = allSubscribed.filter(
    (entry) => entry.hasOpenBuy || entry.hasOpenSell
  );

  return { allSubscribed, manuallySubscribed, withOpenTrades };
};

export const useSubscribedSymbolsStore = create<SubscribedSymbolsStore>(
  (set, get) => {
    // Create stable function references
    const getSubscribedSymbol = (currency: string) =>
      get().byCurrency.get(currency);
    const isSubscribed = (currency: string) => get().byCurrency.has(currency);
    const requestSignalRSubscribe = (currency: string) => {
      import("../../connection/client/VybeTraderClient").then(
        ({ VybeTraderClient }) => {
          const client = VybeTraderClient.getInstance();
          if (client.isConnected()) {
            client
              .subscribeToSymbol(currency)
              .catch((error) => {
                get().setSignalRSubscriptionStatus(currency, false);
                console.error(
                  `[SubscribedSymbols] Failed to subscribe to ${currency} via SignalR:`,
                  error
                );
              });
          } else {
            get().setSignalRSubscriptionStatus(currency, false);
          }
        }
      );
    };
    const canUnsubscribe = (currency: string) => {
      const entry = get().byCurrency.get(currency);
      return entry ? !(entry.hasOpenBuy || entry.hasOpenSell) : false;
    };

    // Get multiple subscribed symbols by currencies
    const getSubscribedSymbolsByCurrencies = (
      currencies: string[]
    ): SubscribedSymbolEntry[] => {
      const state = get();
      const entries: SubscribedSymbolEntry[] = [];

      for (const currency of currencies) {
        const entry = state.byCurrency.get(currency);
        if (entry) {
          entries.push(entry);
        }
      }

      return entries;
    };

    return {
      ...initialState,
      // Start with isInitialized: true since we can operate even with empty state
      isInitialized: true,

      subscribe: (symbol: Symbol) => {
        let shouldSubscribeViaSignalR = false;

        set((state) => {
          const newByCurrency = new Map(state.byCurrency);
          const existing = newByCurrency.get(symbol.currency);
          const now = new Date();
          shouldSubscribeViaSignalR = !(
            existing?.isSignalRSubscribed || existing?.isSignalRSubscribing
          );

          if (existing) {
            if (existing.isManuallySubscribed && !shouldSubscribeViaSignalR) {
              return state;
            }

            // Update existing entry to mark as manually subscribed
            newByCurrency.set(symbol.currency, {
              ...existing,
              isManuallySubscribed: true,
              isSignalRSubscribing: shouldSubscribeViaSignalR
                ? true
                : existing.isSignalRSubscribing,
              lastUpdated: now,
            });
          } else {
            // Create new entry
            newByCurrency.set(symbol.currency, {
              symbol,
              isManuallySubscribed: true,
              hasOpenBuy: false,
              hasOpenSell: false,
              isSignalRSubscribed: false,
              isSignalRSubscribing: shouldSubscribeViaSignalR,
              dateAdded: now,
              lastUpdated: now,
            });
          }

          const arrays = computeArrays(newByCurrency);
          const newState = {
            ...state,
            byCurrency: newByCurrency,
            lastSyncDate: now,
            ...arrays,
          };

          return newState;
        });

        if (shouldSubscribeViaSignalR) {
          requestSignalRSubscribe(symbol.currency);
        }
      },
      unsubscribe: (currency: string) => {
        set((state) => {
          const existing = state.byCurrency.get(currency);
          if (!existing) {
            console.warn(
              `[SubscribedSymbols] Cannot unsubscribe from ${currency}: not found`
            );
            return state;
          }

          if (existing.hasOpenBuy || existing.hasOpenSell) {
            console.warn(
              `[SubscribedSymbols] Cannot unsubscribe from ${currency}: has open trades`
            );
            return state;
          }

          const newByCurrency = new Map(state.byCurrency);

          if (existing.hasOpenBuy || existing.hasOpenSell) {
            // Keep the symbol but mark as not manually subscribed
            newByCurrency.set(currency, {
              ...existing,
              isManuallySubscribed: false,
              lastUpdated: new Date(),
            });
          } else {
            // Remove completely if no trades
            newByCurrency.delete(currency);
          }

          const arrays = computeArrays(newByCurrency);
          const newState = {
            ...state,
            byCurrency: newByCurrency,
            lastSyncDate: new Date(),
            ...arrays,
          };

          // Unsubscribe via SignalR if connected and completely removed
          if (!newByCurrency.has(currency)) {
            import("../../connection/client/VybeTraderClient").then(
              ({ VybeTraderClient }) => {
                const client = VybeTraderClient.getInstance();
                if (client.isConnected()) {
                  client
                    .unsubscribeFromSymbol(currency)
                    .then(() => {})
                    .catch((error) => {
                      console.error(
                        `[SubscribedSymbols] Failed to unsubscribe from ${currency} via SignalR:`,
                        error
                      );
                    });
                }
              }
            );
          }

          return newState;
        });
      },
      addSymbolForTrade: (symbol: Symbol, isBuy: boolean) => {
        let shouldSubscribeViaSignalR = false;

        set((state) => {
          const newByCurrency = new Map(state.byCurrency);
          const existing = newByCurrency.get(symbol.currency);
          const now = new Date();
          shouldSubscribeViaSignalR = !(
            existing?.isSignalRSubscribed || existing?.isSignalRSubscribing
          );

          if (existing) {
            // Update existing entry to mark as having trades
            newByCurrency.set(symbol.currency, {
              ...existing,
              hasOpenBuy: isBuy ? true : existing.hasOpenBuy,
              hasOpenSell: !isBuy ? true : existing.hasOpenSell,
              isSignalRSubscribing: shouldSubscribeViaSignalR
                ? true
                : existing.isSignalRSubscribing,
              lastUpdated: now,
            });
          } else {
            // Create new entry for trade
            newByCurrency.set(symbol.currency, {
              symbol,
              isManuallySubscribed: false,
              hasOpenBuy: isBuy,
              hasOpenSell: !isBuy,
              isSignalRSubscribed: false,
              isSignalRSubscribing: shouldSubscribeViaSignalR,
              dateAdded: now,
              lastUpdated: now,
            });
          }

          const arrays = computeArrays(newByCurrency);
          const newState = {
            ...state,
            byCurrency: newByCurrency,
            lastSyncDate: now,
            ...arrays,
          };

          return newState;
        });

        if (shouldSubscribeViaSignalR) {
          requestSignalRSubscribe(symbol.currency);
        }
      },
      updateTradeStatus: (
        currency: string,
        hasBuy: boolean,
        hasSell: boolean
      ) => {
        set((state) => {
          const existing = state.byCurrency.get(currency);
          if (!existing) {
            console.warn(
              `[SubscribedSymbols] Cannot update trade status for ${currency}: not found`
            );
            return state;
          }

          const newByCurrency = new Map(state.byCurrency);

          // Update the trade status
          newByCurrency.set(currency, {
            ...existing,
            hasOpenBuy: hasBuy,
            hasOpenSell: hasSell,
            lastUpdated: new Date(),
          });

          const arrays = computeArrays(newByCurrency);
          const newState = {
            ...state,
            byCurrency: newByCurrency,
            lastSyncDate: new Date(),
            ...arrays,
          };

          return newState;
        });
      },
      initializeFromWatchlist: (symbols: Symbol[]) => {
        set((state) => {
          const newByCurrency = new Map(state.byCurrency);
          const now = new Date();

          symbols.forEach((symbol) => {
            const existing = newByCurrency.get(symbol.currency);

            if (!existing) {
              // Create new entry for watchlist symbol
              newByCurrency.set(symbol.currency, {
                symbol,
                isManuallySubscribed: true, // Watchlist symbols are considered manually subscribed
                hasOpenBuy: false,
                hasOpenSell: false,
                isSignalRSubscribed: false,
                dateAdded: now,
                lastUpdated: now,
              });
            }
            // If already exists, don't override (could be from trades)
          });

          const arrays = computeArrays(newByCurrency);
          const newState = {
            ...state,
            byCurrency: newByCurrency,
            isInitialized: true,
            lastSyncDate: now,
            ...arrays,
          };

          return newState;
        });
      },

      initializeFromOpenTrades: (
        tradesData: { symbol: Symbol; isBuy: boolean }[]
      ) => {
        set((state) => {
          const newByCurrency = new Map(state.byCurrency);
          const now = new Date();

          // Group trades by symbol to count buy/sell
          const tradesBySymbol = new Map<
            string,
            { symbol: Symbol; buyCount: number; sellCount: number }
          >();

          tradesData.forEach(({ symbol, isBuy }) => {
            const existing = tradesBySymbol.get(symbol.currency);
            if (existing) {
              if (isBuy) {
                existing.buyCount++;
              } else {
                existing.sellCount++;
              }
            } else {
              tradesBySymbol.set(symbol.currency, {
                symbol,
                buyCount: isBuy ? 1 : 0,
                sellCount: isBuy ? 0 : 1,
              });
            }
          });

          // Now update the store entries
          tradesBySymbol.forEach(
            ({ symbol, buyCount, sellCount }, currency) => {
              const existing = newByCurrency.get(currency);

              if (existing) {
                // Update existing entry to mark as having trades
                newByCurrency.set(currency, {
                  ...existing,
                  symbol, // Update symbol data in case it changed
                  hasOpenBuy: buyCount > 0,
                  hasOpenSell: sellCount > 0,
                  lastUpdated: now,
                });
              } else {
                // Create new entry for trade
                newByCurrency.set(currency, {
                  symbol,
                  isManuallySubscribed: false,
                  hasOpenBuy: buyCount > 0,
                  hasOpenSell: sellCount > 0,
                  isSignalRSubscribed: false,
                  dateAdded: now,
                  lastUpdated: now,
                });
              }
            }
          );

          const arrays = computeArrays(newByCurrency);
          const newState = {
            ...state,
            byCurrency: newByCurrency,
            isInitialized: true,
            lastSyncDate: now,
            ...arrays,
          };

          return newState;
        });
      },
      reset: () => {
        set(initialState);
      },

      initializeAndSubscribe: (symbols: Symbol[]) => {
        const symbolsToSubscribe: string[] = [];

        set((state) => {
          const newByCurrency = new Map(state.byCurrency);
          const now = new Date();

          symbols.forEach((symbol) => {
            const existing = newByCurrency.get(symbol.currency);
            const shouldSubscribe = !(
              existing?.isSignalRSubscribed || existing?.isSignalRSubscribing
            );

            if (!existing) {
              // Create new entry for watchlist symbol
              newByCurrency.set(symbol.currency, {
                symbol,
                isManuallySubscribed: true,
                hasOpenBuy: false,
                hasOpenSell: false,
                isSignalRSubscribed: false,
                isSignalRSubscribing: shouldSubscribe,
                dateAdded: now,
                lastUpdated: now,
              });
              if (shouldSubscribe) symbolsToSubscribe.push(symbol.currency);
            } else if (!existing.isManuallySubscribed) {
              // Update existing to be manually subscribed
              newByCurrency.set(symbol.currency, {
                ...existing,
                isManuallySubscribed: true,
                isSignalRSubscribing: shouldSubscribe
                  ? true
                  : existing.isSignalRSubscribing,
                lastUpdated: now,
              });
              if (shouldSubscribe) symbolsToSubscribe.push(symbol.currency);
            }
          });

          const arrays = computeArrays(newByCurrency);
          const newState = {
            ...state,
            byCurrency: newByCurrency,
            isInitialized: true,
            lastSyncDate: now,
            ...arrays,
          };

          return newState;
        });

        symbolsToSubscribe.forEach(requestSignalRSubscribe);
      },

      updateQuote: (quote: Quote) => {
        set((state) => {
          const existing = state.byCurrency.get(quote.symbol);
          if (!existing) {
            // Quote key doesn't match any subscribed symbol (server/catalog key divergence).
            // Log once per unknown key so mismatches aren't silently invisible, without tick-rate spam.
            if (!unknownQuoteKeys.has(quote.symbol)) {
              unknownQuoteKeys.add(quote.symbol);
              console.debug(
                `[SubscribedSymbols] dropping quote for unknown symbol key "${quote.symbol}" (not in byCurrency)`,
              );
            }
            return state;
          }

          // Skip update if quote hasn't changed (avoid infinite loops)
          const currentQuote = existing.quote;
          if (currentQuote && 
              currentQuote.bid === quote.bid && 
              currentQuote.ask === quote.ask && 
              currentQuote.last === quote.last) {
            return state; // Return current state unchanged
          }

          // Preserve the prior bid/ask/last so the watchlist can color each
          // price cell by last-tick direction (matches OpenPositionLiveCells).
          const quoteWithPrev: Quote = {
            ...quote,
            prevBid: currentQuote?.bid ?? quote.bid,
            prevAsk: currentQuote?.ask ?? quote.ask,
            prevLast: currentQuote?.last ?? quote.last,
          };

          const newByCurrency = new Map(state.byCurrency);
          newByCurrency.set(quote.symbol, {
            ...existing,
            quote: quoteWithPrev,
            lastUpdated: new Date(),
            lastMoveAt: currentQuote ? Date.now() : existing.lastMoveAt,
          });

          // DON'T recompute arrays for quotes!
          // This was causing massive performance issues
          const newState = {
            ...state,
            byCurrency: newByCurrency,
            // Keep existing arrays - quotes don't change structure
          };

          return newState;
        });
      },
      setSignalRSubscriptionStatus: (
        currency: string,
        isSubscribed: boolean
      ) => {
        set((state) => {
          const existing = state.byCurrency.get(currency);
          if (!existing) {
            console.warn(
              `[SubscribedSymbols] Cannot set SignalR status for ${currency}: not found`
            );
            return state;
          }

          const newByCurrency = new Map(state.byCurrency);
          newByCurrency.set(currency, {
            ...existing,
            isSignalRSubscribed: isSubscribed,
            isSignalRSubscribing: false,
            lastUpdated: new Date(),
          });

          const arrays = computeArrays(newByCurrency);
          const newState = {
            ...state,
            byCurrency: newByCurrency,
            ...arrays,
          };

          return newState;
        });
      },

      validateAgainstAvailableSymbols: (
        availableSymbols: Map<string, Symbol>
      ) => {
        set((state) => {
          const newByCurrency = new Map(state.byCurrency);
          let removedCount = 0;
          const removedSymbols: string[] = [];

          // Check each subscribed symbol against available symbols
          for (const [currency, entry] of newByCurrency.entries()) {
            if (!availableSymbols.has(currency)) {
              // Symbol no longer available - remove it regardless of trade status
              newByCurrency.delete(currency);
              removedCount++;
              removedSymbols.push(currency);
              console.warn(
                `[SubscribedSymbols] Removing unavailable symbol: ${currency}`
              );
            } else {
              // Update symbol data in case it changed
              const updatedSymbol = availableSymbols.get(currency)!;
              newByCurrency.set(currency, {
                ...entry,
                symbol: updatedSymbol,
                lastUpdated: new Date(),
              });
            }
          }

          if (removedCount > 0) {
            const arrays = computeArrays(newByCurrency);
            const newState = {
              ...state,
              byCurrency: newByCurrency,
              lastSyncDate: new Date(),
              ...arrays,
            };

            return newState;
          } else {
            return state;
          }
        });
      },

      // Stable function references
      getSubscribedSymbol,
      isSubscribed,
      canUnsubscribe,
      getSubscribedSymbolsByCurrencies,
    };
  }
);
